import React, { useEffect, useState } from "react";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer 
} from "recharts";
import config from "../config.js";

const BudgetLineChart = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = config;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const response = await fetch(`${API_BASE_URL}/api/v1/workbookToJson`);
        const responseData = await response.json();
        
        if (!responseData.success || !responseData.data || !responseData.data.Sheet1) {
          throw new Error("Data format is invalid or the request was unsuccessful");
        }

        const rawData = responseData.data.Sheet1;
       
        const filteredData = rawData.filter((item, index) => 
          index !== 0 && 
          item["Personal Budget"] !== "Total" && 
          typeof item["__EMPTY"] === "number"
        );
       
        const formattedData = filteredData.map(item => ({
          id: item["Personal Budget"] || "",
          expense: item["__EMPTY_1"] || "",
          amount: item["__EMPTY"] || 0,
          type: item["__EMPTY_2"] || ""
        }));

        setChartData(formattedData);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message || "Failed to load chart data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center bg-gray-100 rounded p-4 mt-4">
        <h3 className="text-lg font-bold text-red-500 mb-2">Error Loading Chart</h3>
        <p>{error}</p>
      </div>
    );
  } 
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="mt-2">Loading chart data...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto">
      <h2 className="text-2xl font-bold text-center my-4">Personal Budget Expenses</h2>
      
      {chartData.length === 0 ? (
        <div className="text-center p-8 bg-gray-50 rounded">
          <p>No expense data available</p>
        </div>
      ) : (
        <div className="bg-white p-4 rounded shadow border border-gray-200">
          <ResponsiveContainer width="100%" height={500}>
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="expense" 
                angle={-45} 
                textAnchor="end" 
                height={80}
              />
              <YAxis 
                label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft', offset: -5 }}
              />
              <Tooltip formatter={(value) => [`$${value}`, 'Amount']} />
              <Legend wrapperStyle={{ bottom: -10 }} />
              <Line 
                type="monotone" 
                dataKey="amount" 
                stroke="#4BC0C0" 
                strokeWidth={2}
                activeDot={{ r: 8 }} 
                name="Amount ($)" 
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default BudgetLineChart;
